import { type AvailableCoupon, type AvailableCouponCriterion, type AvailableCriterionGroup, CouponType, PromotionType } from '@hooks-api/types/dataModel/coupon'
import { type Ticket } from '@hooks-api/types/dataModel/ticket'
import { formatPrice } from '@utils/formatCurrency'
import { type TFunction } from 'i18next'
import { getCouponDisplayInfo } from './getCouponDisplayInfo'
import { getDiscountPercentage } from './getDiscountPercentage'

const AmountUnitI18nKeyMap: Record<string, string> = {
  件: 'coupon.amount',
  個: 'coupon.piece',
  罐: 'coupon.can',
  箱: 'coupon.box',
  包: 'coupon.bag',
}

const getCouponTypeWording = (couponType: CouponType, t: TFunction): string => {
  switch (couponType) {
    case CouponType.ALL_PRODUCT:
      return t('coupon.sitewide', { defaultValue: '全館' })
    case CouponType.WAREHOUSE_PRODUCT:
      return t('coupon.subscribeFor', { defaultValue: '寄倉' })
    default:
      return ''
  }
}

const getCouponConsumptionTypeWording = (firstCriterion: AvailableCouponCriterion, amountUnit: string, t: TFunction, currency?: string): string => {
  /**
   * 滿額
   */
  if (firstCriterion.criterion_amount && firstCriterion.criterion_amount > 0) {
    return t('coupon.spend', { criterionAmount: formatPrice(currency, firstCriterion.criterion_amount), defaultValue: '滿 {{criterionAmount}}' })
  }
  /**
   * 滿件
   */
  if (firstCriterion.criterion_quantity && firstCriterion.criterion_quantity > 0) {
    return (AmountUnitI18nKeyMap[amountUnit] && t(AmountUnitI18nKeyMap[amountUnit], { criterionQuantity: firstCriterion.criterion_quantity, defaultValue: `{{criterionQuantity}} ${amountUnit}` })) ?? ''
  }

  return ''
}

const getCouponPromotionTypeWording = (criterionGroup: AvailableCriterionGroup, firstCriterion: AvailableCouponCriterion, t: TFunction, currency?: string): string => {
  if (criterionGroup.promotion_type === PromotionType.FIXED_PRICE && firstCriterion.fixed_price) {
    return t('coupon.specialPrice', { fixPrice: formatPrice(currency, firstCriterion.fixed_price), defaultValue: '{{fixPrice}}' })
  }
  if (criterionGroup.promotion_type === PromotionType.DISCOUNT_PRICE && firstCriterion.discount_amount) {
    return t('coupon.getPriceOff', { discountAmount: formatPrice(currency, firstCriterion.discount_amount), defaultValue: '-{{discountAmount}}' })
  }
  if (criterionGroup.promotion_type === PromotionType.DISCOUNT_RATE && firstCriterion.discount_rate) {
    const discountPercentage = getDiscountPercentage(firstCriterion.discount_rate)
    /**
     * 英文翻譯用 eg: 5% off -> {{discountRate}}% off
     */
    const discountRate = firstCriterion.discount_rate
    const discountPercentageWording = discountPercentage === 0 ? t('coupon.free', { defaultValue: '免費' }) : t('coupon.getPercentageOff', { percentage: discountPercentage, discountRate, defaultValue: '{{percentage}} 折' })

    if (firstCriterion.m_quantity && firstCriterion.m_quantity > 0) {
      return `${t('coupon.andQuantity', { quantity: firstCriterion.m_quantity, defaultValue: '其中 {{quantity}} 件' })} ${discountPercentageWording}`
    }

    return discountPercentageWording
  }
  if (criterionGroup.promotion_type === PromotionType.GIVEAWAY) {
    return t('coupon.getFreeGift', { defaultValue: '送贈品' })
  }
  if (criterionGroup.promotion_type === PromotionType.DIS_SHIPPING && firstCriterion.dis_shipping_amount) {
    return t('coupon.shippingDiscount', { defaultValue: '運費優惠' })
  }
  if (criterionGroup.promotion_type === PromotionType.FREE_SHIPPING) {
    return t('coupon.freeShipping', { defaultValue: '免運' })
  }
  return ''
}

/**
 * 單門檻: 消費商品 + 消費類型 + 折扣類型
 * 多門檻: 一律只顯示最低門檻的消費商品 + 消費類型 + 折扣類型
 * https://www.notion.so/shepherdtech/2503-1bca4b7afc8880e48410d27c2ab99d03?pvs=4#1bda4b7afc8880eb8363e344b9d3812b
 */
export const composeCouponTagLabel = (coupon: AvailableCoupon, directPurchaseAmountUnit: string, staggeredShippingAmountUnit: string, t: TFunction, currency?: string): string => {
  const displayInfo = getCouponDisplayInfo(coupon.criterion_groups)
  if (!displayInfo) {
    return ''
  }
  const { criterionGroup, firstCriterion } = displayInfo
  /**
   * 多條件 (A+B)
   */
  if (coupon.criterion_groups.length > 1) {
    return `${t('coupon.crossZone', { defaultValue: '跨區組合' })} ${getCouponPromotionTypeWording(criterionGroup, firstCriterion, t, currency)}`
  }
  const amountUnit = criterionGroup.type === CouponType.WAREHOUSE_PRODUCT ? staggeredShippingAmountUnit : directPurchaseAmountUnit

  return `${getCouponTypeWording(criterionGroup.type, t)} ${getCouponConsumptionTypeWording(firstCriterion, amountUnit, t, currency)} ${getCouponPromotionTypeWording(criterionGroup, firstCriterion, t, currency)}`
}

export const composeCouponConditionLabel = (coupon: AvailableCoupon | Ticket, amountUnit: string, t: TFunction, currency?: string): string[] => {
  const displayInfo = getCouponDisplayInfo(coupon.criterion_groups)
  if (!displayInfo) {
    return ['']
  }
  const { criterionGroup, firstCriterion, criterions } = displayInfo
  /**
   * 多條件 (A+B)
   */
  if (coupon.criterion_groups.length > 1) {
    return [
      `${t('coupon.crossZone', { defaultValue: '跨區組合' })} ${getCouponPromotionTypeWording(criterionGroup, firstCriterion, t, currency)}`,
    ]
  }

  return criterions.map((criterion) => {
    return `${getCouponConsumptionTypeWording(criterion, amountUnit, t, currency)} ${getCouponPromotionTypeWording(criterionGroup, criterion, t, currency)}`
  })
}

/**
 * 簡略版：滿額/滿件
 */
const getCouponConsumptionTypeWordingBrief = (firstCriterion: AvailableCouponCriterion, t: TFunction) => {
  /**
   * 滿額
   */
  if (firstCriterion.criterion_amount && firstCriterion.criterion_amount > 0) {
    return t('product.multiBuy', { defaultValue: '滿額' })
  }
  /**
   * 滿件
   */
  if (firstCriterion.criterion_quantity && firstCriterion.criterion_quantity > 0) {
    return t('product.spendAndSave', { defaultValue: '滿件' })
  }

  return ''
}

/**
 * 簡略版：只顯示折扣類型和數值，不包含「折」「折抵」等文字前綴
 */
const getCouponPromotionTypeWordingBrief = (criterionGroup: AvailableCriterionGroup, firstCriterion: AvailableCouponCriterion, t: TFunction, currency?: string) => {
  if (criterionGroup.promotion_type === PromotionType.FIXED_PRICE && firstCriterion.fixed_price) {
    return formatPrice(currency, firstCriterion.fixed_price)
  }

  if (criterionGroup.promotion_type === PromotionType.DISCOUNT_PRICE && firstCriterion.discount_amount) {
    return t('product.amountOff', { discountAmount: formatPrice(currency, firstCriterion.discount_amount), defaultValue: '-{{discountAmount}}' })
  }

  if (criterionGroup.promotion_type === PromotionType.DISCOUNT_RATE && firstCriterion.discount_rate) {
    const discountPercentage = getDiscountPercentage(firstCriterion.discount_rate)
    const discountRate = firstCriterion.discount_rate
    return discountPercentage === 0 ? t('product.free', { defaultValue: '免費' }) : t('product.discount', { percentage: discountPercentage, discountRate, defaultValue: '{{percentage}} 折' })
  }

  return ''
}

/**
 * 簡略版優惠券標籤規則（提供給收藏商品與補貨通知）：
 * - 前綴
 *   - 滿件/滿額：不顯示具體數值
 *   - A+B：顯示「跨區」
 * - 後綴
 *   - 折扣：顯示「9 折」、「-$10」、「$10」
 * 贈品：一律只顯示「贈品」（不加滿額/滿件前綴）
 * 不顯示運費券和寄倉券
 */
export const composeCouponTagLabelBrief = (coupon: AvailableCoupon, t: TFunction, currency?: string) => {
  const displayInfo = getCouponDisplayInfo(coupon.criterion_groups)
  if (!displayInfo) {
    return ''
  }
  const { criterionGroup, firstCriterion } = displayInfo

  if (criterionGroup.promotion_type === PromotionType.GIVEAWAY) {
    return t('product.freeGift', { defaultValue: '贈品' })
  }

  /**
   * 多條件 (A+B)
   */
  if (coupon.criterion_groups.length > 1) {
    return t('product.couponBriefLabel', {
      consumptionType: t('product.bundleDeal', { defaultValue: '跨區' }),
      promotionType: getCouponPromotionTypeWordingBrief(criterionGroup, firstCriterion, t, currency),
      defaultValue: '{{consumptionType}} {{promotionType}}',
    })
  }

  /**
   * 單條件
   */
  return t('product.couponBriefLabel', {
    consumptionType: getCouponConsumptionTypeWordingBrief(firstCriterion, t),
    promotionType: getCouponPromotionTypeWordingBrief(criterionGroup, firstCriterion, t, currency),
    defaultValue: '{{consumptionType}} {{promotionType}}',
  })
}
